import React, { useState } from 'react';
import { Box } from '@material-ui/core';
import { LoadScript, GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import RenderPins from '../components/RenderPins';
import RenderPolygons from '../components/RenderPolygons';
import RenderLines from '../components/RenderLines';
import FabSpeedDial from '../FabSpeedDial';
import MapFeatureDialog from './AddMapContentDialog';
import { Edit, MyLocation } from '@mui/icons-material';
import CustomMapControl from './CustomMapControl';
import { useToast } from 'context/tostifyContext';
import { IconButton, InputBase, Paper } from '@mui/material';
import { Close, Search } from '@material-ui/icons';

const containerStyle = {
  width: '100%',
  height: '100%',
};

function MapEditor({
  mapState,
  mapActions,
  mapData,
  saveMapCoordinates,
  updateMapCoordinates,
  deleteMapCoordinates,
  boundaryAlreadyExists,
  mapHeight = '75vh',
}) {
  const {
    center,
    zoom,
    pins,
    polygons,
    lines,
    currentLine,
    currentPolygon,
    currentPin,
    selected,
    currentPolygonData,
    currentLineData,
    showPin,
    showPolygonDialog,
    showLineDialog,
    mode,
    isPointInsidePolygon,
    map,
    searchText,
    searchOpen,
    userLocation,
  } = mapState;

  const {
    setSelected,
    setShowPin,
    setShowPolygonDialog,
    setShowLineDialog,
    setCurrentPin,
    setCurrentPolygonData,
    setCurrentLineData,
    setMode,
    setLines,
    setCurrentLine,
    handleMapPress,
    generateId,
    calculateLineDistance,
    setCurrentPolygon,
    setPolygons,
    setMap,
    setPins,
    setSearchText,
    handleSearch,
    setSearchOpen,
    goToUserLocation,
    handleMapLoad,
    handleZoomChanged,
  } = mapActions;

  const { showToast } = useToast();

  return (
    <>
      <Box flex={6} sx={{ height: mapHeight }}>
        <LoadScript googleMapsApiKey="AIzaSyA94J1dEXShLTlOZmIZbmOT31Qyo2g-2OU">
          <div id="map-fullscreen-container" style={{ position: 'relative', height: '100%' }}>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={zoom}
              onZoomChanged={handleZoomChanged}
              options={{
                mapTypeId: 'hybrid',
                streetViewControl: false,
                mapTypeControl: false,
                rotateControl: false,
                clickableIcons: false,
              }}
              onClick={handleMapPress}
              onLoad={map => handleMapLoad(map)}
              onUnmount={() => handleMapLoad(null)}>
              {map && (
                <CustomMapControl map={map} position={window.google.maps.ControlPosition.TOP_RIGHT}>
                  <Paper
                    elevation={4}
                    sx={{
                      mt: 1,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'row-reverse', // 👈 key
                      px: 0.5,
                      py: 0.5,
                      borderRadius: '24px',
                    }}>
                    {/* Search Icon (fixed position) */}
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (searchOpen && searchText.trim()) handleSearch();
                        setSearchOpen(prev => !prev);
                      }}>
                      <Search />
                    </IconButton>

                    {/* Expanding input (opens LEFT) */}
                    <Box
                      sx={{
                        width: searchOpen ? 260 : 0,
                        transition: 'width 0.3s ease',
                        overflow: 'hidden',
                      }}>
                      <InputBase
                        autoFocus
                        placeholder="City, lat lng or link"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        sx={{
                          ml: 1,
                          fontSize: 14,
                          width: '100%',
                        }}
                      />
                    </Box>

                    {/* Clear icon */}
                    {searchOpen && searchText && (
                      <IconButton size="small" onClick={() => setSearchText('')}>
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>
                </CustomMapControl>
              )}

              {map && (
                <CustomMapControl map={map} position={window?.google?.maps?.ControlPosition?.RIGHT_TOP}>
                  <Paper
                    elevation={4}
                    sx={{
                      mt: 1,
                      mr: 1,
                      borderRadius: '50%',
                    }}>
                    <IconButton onClick={goToUserLocation} size="small">
                      <MyLocation />
                    </IconButton>
                  </Paper>
                </CustomMapControl>
              )}

              {userLocation && (
                <Marker
                  position={userLocation}
                  icon={{
                    path: window?.google?.maps?.SymbolPath?.CIRCLE,
                    scale: 8,
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: 'white',
                  }}
                />
              )}

              {selected && (
                <InfoWindow
                  position={{
                    lat: selected.latitude || Math.max(...(selected.coords?.map(c => c.lat) ?? [0])),
                    lng:
                      selected.longitude ||
                      (selected.coords?.reduce((top, c) => (c.lat > top.lat ? c : top), selected.coords[0])?.lng ?? 0),
                  }}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -10),
                    disableAutoPan: true,
                  }}
                  onCloseClick={() => setSelected(null)}>
                  <div style={{ background: 'white', padding: '6px', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <strong style={{ color: 'black' }}>{selected?.displayName || selected?.mapLabel}</strong>
                      <button
                        onClick={() => {
                          if (selected?.mapType === 'pin') {
                            setShowPin(true);
                            setCurrentPin(selected);
                          } else if (selected?.mapType === 'polygon') {
                            setShowPolygonDialog(true);
                            setCurrentPolygonData(selected);
                          } else if (selected?.mapType === 'line') {
                            setShowLineDialog(true);
                            setCurrentLineData(selected);
                          }
                        }}
                        style={{
                          marginLeft: 8,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                        }}
                        aria-label="Edit map feature">
                        <Edit />
                      </button>
                    </div>
                    {/* Additional info for polygon or line */}
                    {selected?.mapType === 'polygon' && <p style={{ margin: 0 }}>{selected.area} Ha</p>}
                    {selected?.mapType === 'line' && <p style={{ margin: 0 }}>{selected.length} km</p>}
                  </div>
                </InfoWindow>
              )}

              <RenderPins
                pins={pins}
                selected={selected}
                setSelected={setSelected}
                setShowPin={setShowPin}
                setCurrentPin={setCurrentPin}
                mode={mode}
                setPins={setPins}
                onUpdatePin={updatedPin => updateMapCoordinates(updatedPin, 'pin')}
              />
              <RenderPolygons
                polygons={polygons}
                selected={selected}
                setSelected={setSelected}
                currentPolygon={currentPolygon}
                mode={mode}
                setPolygons={setPolygons}
                setCurrentPolygonData={setCurrentPolygonData}
                setCurrentPolygon={setCurrentPolygon}
                setShowPolygonDialog={setShowPolygonDialog}
                onUpdatePolygon={updatedPoly => updateMapCoordinates(updatedPoly, 'polygon')}
                zoom={zoom}
              />
              <RenderLines
                lines={lines}
                selected={selected}
                setSelected={setSelected}
                calculateLineDistance={calculateLineDistance}
                currentLine={currentLine}
                setCurrentLine={setCurrentLine}
                mode={mode}
                setLines={setLines}
                onUpdateLine={updatedLine => updateMapCoordinates(updatedLine, 'line')}
                zoom={zoom}
              />

              {/* Add FabSpeedDial as custom control */}
              {map && (
                <CustomMapControl map={map} position={window.google.maps.ControlPosition.RIGHT_BOTTOM}>
                  <div style={{ marginRight: 10, marginBottom: 10 }}>
                    <FabSpeedDial handleMode={setMode} />
                  </div>
                </CustomMapControl>
              )}

              {/* Add Done button as custom control */}
              {map && mode !== null && (
                <CustomMapControl map={map} position={window.google.maps.ControlPosition.BOTTOM_CENTER}>
                  <button
                    onClick={() => {
                      if (mode === 'Line' && currentLine.length >= 2) {
                        const newLine = {
                          id: generateId(),
                          mapLabel: '',
                          hexColor: 'blue',
                          coords: [...currentLine],
                          length: calculateLineDistance(currentLine),
                        };
                        setLines(prev => [...prev, newLine]);
                        setCurrentLineData(newLine);
                        setCurrentLine([]);
                        setShowLineDialog(true);
                      }
                      setMode(null);
                    }}
                    style={{
                      marginBottom: 20,
                      padding: '10px 20px',
                      backgroundColor: '#007BFF',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    }}>
                    Done
                  </button>
                </CustomMapControl>
              )}
            </GoogleMap>
          </div>
        </LoadScript>
      </Box>

      {/* Map Feature Dialogs */}
      <MapFeatureDialog
        visible={showPin}
        mapData={mapData}
        onClose={() => setShowPin(false)}
        onSave={async (updatedFeature, originalLabel) => {
          let matchedPolygon = null;
          const coordinate = { latitude: updatedFeature?.latitude, longitude: updatedFeature?.longitude };

          if (updatedFeature?.mapLabel?.startsWith('Sample')) {
            for (const polygon of polygons.filter(p => p.mapLabel?.startsWith('Sub'))) {
              if (isPointInsidePolygon(coordinate, polygon.coords)) {
                matchedPolygon = polygon;
                break;
              }
            }

            if (!matchedPolygon) {
              showToast('Sample point must be inside a sub compartment', 'error');
              return;
            }
          }

          const updated = {
            ...updatedFeature,
            relatedTo: matchedPolygon?.mapLabel,
          };

          await saveMapCoordinates(updated, 'pin', originalLabel);
          setShowPin(false);
          setCurrentPin(null);
          setSelected(null);
        }}
        onDelete={async f => {
          await deleteMapCoordinates(f.id);
          setShowPin(false);
          setCurrentPin(null);
          setSelected(null);
        }}
        type="pin"
        feature={currentPin}
        setFeature={setCurrentPin}
        selectedFeature={selected}
      />

      <MapFeatureDialog
        visible={showPolygonDialog}
        mapData={mapData}
        onClose={() => setShowPolygonDialog(false)}
        onSave={async (updatedFeature, originalLabel) => {
          await saveMapCoordinates(updatedFeature, 'polygon', originalLabel);

          setShowPolygonDialog(false);
          setCurrentPolygonData(null);
          setSelected(null);
        }}
        onDelete={async f => {
          await deleteMapCoordinates(f.id);
          setShowPolygonDialog(false);
          setCurrentPolygonData(null);
          setSelected(null);
        }}
        type="polygon"
        feature={currentPolygonData}
        setFeature={setCurrentPolygonData}
        selectedFeature={selected}
        boundaryAlreadyExists={boundaryAlreadyExists}
      />

      <MapFeatureDialog
        visible={showLineDialog}
        mapData={mapData}
        onClose={() => setShowLineDialog(false)}
        onSave={async (updatedFeature, originalLabel) => {
          await saveMapCoordinates(updatedFeature, 'line', originalLabel);

          setShowLineDialog(false);
          setCurrentLineData(null);
          setSelected(null);
        }}
        onDelete={async f => {
          await deleteMapCoordinates(f.id);
          setShowLineDialog(false);
          setCurrentLineData(null);
          setSelected(null);
        }}
        type="line"
        feature={currentLineData}
        setFeature={setCurrentLineData}
        selectedFeature={selected}
      />
    </>
  );
}

export default MapEditor;
